import { AMBIENT_SCRIBE_NOTE_PARENT_TYPE } from "services/types/ambient-scribe-note.type";

import useLatestScribeNote from "./use-latest-scribe-note.hook";

/**
 * The audiology report summary generated from one recording, for one report (SN-19067).
 *
 * A thin binding of `useLatestScribeNote` to the audiology parent type. It exists so
 * `InsertScribeDialog` (which inserts the note) and `AudiologySummaryTab` (which renders it)
 * cannot drift: identical RTK Query args are what keep them in ONE cache entry, and therefore
 * what stops Insert from writing something other than what is on screen. Call this rather than
 * passing audiology arguments to the generic hook by hand.
 *
 * `withPrompt` is fixed on for both callers even though only the tab displays it — sharing a
 * single cache entry matters more than the payload, and it is one row.
 */

type UseAudiologyReportSummaryNoteArgs = {
  scribeId: number | string | undefined;
  reportId: number | undefined;
  enabled: boolean;
};

export default function useAudiologyReportSummaryNote({
  scribeId,
  reportId,
  enabled
}: UseAudiologyReportSummaryNoteArgs) {
  return useLatestScribeNote({
    scribeId,
    parentType: AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AUDIOLOGY_REPORT,
    parentId: reportId,
    // `useLatestScribeNote` treats a missing `parentId` as "every note of this kind on this scribe",
    // which the SOAP caller wants and this one must never get: one recording can carry summaries for
    // several reports, so an unscoped read could render — and Insert — another report's summary.
    // Enforced here rather than in the hook because that is the SOAP path's normal mode.
    enabled: enabled && reportId != null,
    withPrompt: true
  });
}
